import type { QueryClient } from '@tanstack/react-query';

import type { GraphQueryResponse, TreeNode } from '@/types';

type EntityType = keyof Omit<
  GraphQueryResponse,
  'count' | 'offset' | 'relationships'
>;

/**
 * Optimistically update an entity in all query-executor cache entries.
 * Handles both flat (GraphQueryResponse) and tree (TreeNode[]) cache shapes.
 * The existing invalidation still runs to ensure eventual consistency.
 */
export function updateEntityInQueryExecutorCache(
  queryClient: QueryClient,
  entityType: EntityType,
  updatedEntity: { key: string; [k: string]: unknown }
): void {
  queryClient.setQueriesData<GraphQueryResponse | TreeNode[] | null>(
    {
      predicate: query => query.queryKey[0] === 'query-executor',
    },
    old => {
      if (!old) return old;
      if (Array.isArray(old))
        return updateTreeNodes(old, entityType, updatedEntity);
      return updateFlatResponse(old, entityType, updatedEntity);
    }
  );
}

function updateFlatResponse(
  response: GraphQueryResponse,
  entityType: EntityType,
  updatedEntity: { key: string; [k: string]: unknown }
): GraphQueryResponse {
  const entities = response[entityType];
  if (!Array.isArray(entities)) return response;

  const updatedEntities = entities.map((entity: { key?: string }) => {
    if (entity.key === updatedEntity.key) {
      return { ...entity, ...updatedEntity };
    }
    return entity;
  });

  return { ...response, [entityType]: updatedEntities };
}

function updateTreeNodes(
  trees: TreeNode[],
  entityType: EntityType,
  updatedEntity: { key: string; [k: string]: unknown }
): TreeNode[] {
  return trees.map(tree => {
    const result = tree.node?.result;
    if (!result) return tree;

    const entities = result[entityType];
    if (!Array.isArray(entities)) return tree;

    const hasMatch = entities.some(
      (e: { key?: string }) => e.key === updatedEntity.key
    );
    if (!hasMatch) return tree;

    return {
      ...tree,
      node: {
        ...tree.node,
        result: updateFlatResponse(result, entityType, updatedEntity),
      },
    };
  });
}
